// src/hooks/useHW.js
import { useState, useEffect, useCallback } from 'react';
import { fetchDataFromServer } from '../apiUtils';
import { usePolling } from './usePolling';

const asBool = (v) => {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'string') return v.toLowerCase() === 'true';
  if (typeof v === 'number') return v !== 0;
  return false;
};

export function useHW(currentHW, hwKey, baseSwitches, s005Cfg /* { apikey, var } */) {
  // Állapot
  const [switches, setSwitches] = useState(baseSwitches);
  const [heartbeat, setHeartbeat] = useState(false);
  const [lastRequestTime, setLastRequestTime] = useState('');
  const [serverValue, setServerValue] = useState(30);
  const [responseText, setResponseText] = useState('');
  const [temperatureBMP, setTemperatureBMP] = useState(null);
  const [temperatureAHT, setTemperatureAHT] = useState(null);
  const [pressBMP, setPressBMP] = useState(null);
  const [humiAHT, setHumiAHT] = useState(null);
  const [restartNumber, setRestartNumber] = useState(-1);
  const [heKapu1, setHeKapu1] = useState(false);
  const [heAjto, setHeAjto] = useState(false);
  const [s005, setS005] = useState(false);

  // Műveletek
  const toggleSwitch = useCallback((index) => {
    if (!currentHW.variables[index]) return;
    setSwitches((prev) => {
      const next = prev.map((sw, i) => (i === index ? { ...sw, value: !sw.value } : sw));
      const varName = currentHW.variables[index];
      fetchDataFromServer(currentHW.apikey, varName, 'POST', next[index].value ? 'true' : 'false')
        .then((r) => setResponseText(`Válasz: ${JSON.stringify(r)}`))
        .catch((e) => setResponseText(`Hiba: ${e.message}`));
      return next;
    });
  }, [currentHW.apikey, currentHW.variables]);

  const changeValue = useCallback((delta) => {
    setServerValue((prev) => {
      const n = Math.max(0, prev + delta);
      fetchDataFromServer(currentHW.apikey, 'security_code', 'POST', n)
        .then((r) => setResponseText(`Válasz: ${JSON.stringify(r)}`))
        .catch((e) => setResponseText(`Hiba: ${e.message}`));
      return n;
    });
  }, [currentHW.apikey]);

  const postRestart = useCallback(() => {
    fetchDataFromServer(currentHW.apikey, 'Restart_number', 'POST', 0)
      .then((r) => setResponseText(`Válasz: ${JSON.stringify(r)}`))
      .catch((e) => setResponseText(`Hiba: ${e.message}`));
  }, [currentHW.apikey]);

  const toggleKapu = useCallback(() => {
    setHeKapu1((prev) => {
      const next = !prev;
      fetchDataFromServer(currentHW.apikey, 'HE_kapu_1', 'POST', next ? 'true' : 'false')
        .then((r) => setResponseText(`Válasz: ${JSON.stringify(r)}`))
        .catch((e) => setResponseText(`Hiba: ${e.message}`));
      return next;
    });
  }, [currentHW.apikey]);

  const toggleAjto = useCallback(() => {
    setHeAjto((prev) => {
      const next = !prev;
      fetchDataFromServer(currentHW.apikey, 'He_ajto', 'POST', next ? 'true' : 'false')
        .then((r) => setResponseText(`Válasz: ${JSON.stringify(r)}`))
        .catch((e) => setResponseText(`Hiba: ${e.message}`));
      return next;
    });
  }, [currentHW.apikey]);

  const toggleS005 = useCallback(() => {
    if (!s005Cfg?.apikey || !s005Cfg?.var) return;
    setS005((prev) => {
      const next = !prev;
      fetchDataFromServer(s005Cfg.apikey, s005Cfg.var, 'POST', next ? 'true' : 'false')
        .then((r) => setResponseText(`Válasz: ${JSON.stringify(r)}`))
        .catch((e) => setResponseText(`Hiba: ${e.message}`));
      return next;
    });
  }, [s005Cfg]);

  // Lekérdezések
  const fetchHeartbeat = useCallback(() => {
    fetchDataFromServer(currentHW.apikey, 'HeartBeat', 'GET')
      .then((response) => {
        if (response?.HeartBeat?.value !== undefined) {
          setHeartbeat(asBool(response.HeartBeat.value));
        }
        setLastRequestTime(new Date().toLocaleString());
      })
      .catch(() => {});
  }, [currentHW.apikey]);

  const fetchInitialState = useCallback(() => {
    // Fő HW állapot
    fetchDataFromServer(currentHW.apikey, '', 'POST')
      .then((response) => {
        if (!response) return;

        setSwitches((prev) =>
          prev.map((sw, i) => {
            const varName = currentHW.variables[i];
            const newVal = varName ? asBool(response[varName]?.value) : sw.value;
            return { ...sw, value: newVal };
          })
        );

        const sc = parseInt(response.security_code?.value, 10);
        if (!Number.isNaN(sc)) setServerValue(sc);
        setRestartNumber(Number(response.Restart_number?.value ?? -1));

        if ('HE_kapu_1' in response) setHeKapu1(asBool(response.HE_kapu_1?.value));
        if ('He_ajto'   in response) setHeAjto(asBool(response.He_ajto?.value));

        if (response.Temp_BMP  !== undefined) {
          const v = Number(response.Temp_BMP.value);
          setTemperatureBMP(Number.isFinite(v) ? v : null);
        }
        if (response.Press_BMP !== undefined) {
          const v = Number(response.Press_BMP.value);
          setPressBMP(Number.isFinite(v) ? v : null);
        }
        if (response.Humi_AHT  !== undefined) {
          const v = Number(response.Humi_AHT.value);
          setHumiAHT(Number.isFinite(v) ? v : null);
        }
        if (response.Temp_AHT  !== undefined) {
          const v = Number(response.Temp_AHT.value);
          setTemperatureAHT(Number.isFinite(v) ? v : null);
        }
      })
      .catch(() => {});

    // S-005 állapot
    if (s005Cfg?.apikey && s005Cfg?.var) {
      fetchDataFromServer(s005Cfg.apikey, s005Cfg.var, 'GET')
        .then((resp2) => {
          const raw = (resp2?.[s005Cfg.var]?.value !== undefined)
            ? resp2[s005Cfg.var].value
            : resp2?.[s005Cfg.var];
          if (raw !== undefined) setS005(asBool(raw));
        })
        .catch(() => {});
    }
  }, [currentHW.apikey, currentHW.variables, s005Cfg]);

  // Init + polling
  useEffect(() => { fetchInitialState(); }, [hwKey, fetchInitialState]);
  usePolling(fetchHeartbeat, 2000, [hwKey]);
  usePolling(fetchInitialState, 5000, [hwKey, fetchInitialState]);

  return {
    state: {
      switches, heartbeat, lastRequestTime, serverValue, responseText,
      temperatureBMP, temperatureAHT, pressBMP, humiAHT,
      restartNumber, heKapu1, heAjto, s005,
    },
    actions: {
      toggleSwitch, changeValue, postRestart, toggleKapu, toggleAjto, toggleS005, setResponseText,
    },
  };
}
